import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Mail, Phone, Clock, MapPin, Send, CheckCircle2 } from "lucide-react";

const Contact = () => {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Compose a mailto link so user's mail client opens with prefilled content.
    const subject = encodeURIComponent(
      `[TNPSC Tamil Contact] Message from ${form.name || "Visitor"}`
    );
    const body = encodeURIComponent(
      `From: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:tnpsctamilapp@gmail.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <main data-testid="contact-page" className="pt-32 pb-20">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-10">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-amber-600">
          {t.contact.eyebrow}
        </span>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-blue-950 leading-[1.0]">
          {t.contact.title}
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
          {t.contact.subtitle}
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <a
              href="mailto:tnpsctamilapp@gmail.com"
              data-testid="contact-email"
              className="card-lift block bg-white border border-blue-900/10 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center">
                  <Mail size={22} />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-wider uppercase text-amber-600">
                    {t.contact.emailLabel}
                  </p>
                  <p className="mt-1 text-lg font-bold text-blue-950 break-all">
                    tnpsctamilapp@gmail.com
                  </p>
                </div>
              </div>
            </a>

            <a
              href="tel:+919842751334"
              data-testid="contact-phone"
              className="card-lift block bg-white border border-blue-900/10 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                  <Phone size={22} />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-wider uppercase text-amber-600">
                    {t.contact.phoneLabel}
                  </p>
                  <p className="mt-1 text-lg font-bold text-blue-950">
                    +91 98427 51334
                  </p>
                </div>
              </div>
            </a>

            <div className="bg-white border border-blue-900/10 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Clock size={22} />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-wider uppercase text-amber-600">
                    {t.contact.hoursLabel}
                  </p>
                  <p className="mt-1 text-base font-semibold text-blue-950">
                    {t.contact.hours}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-blue-900/10 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-red-100 text-red-700 flex items-center justify-center">
                  <MapPin size={22} />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-wider uppercase text-amber-600">
                    Location
                  </p>
                  <p className="mt-1 text-base font-semibold text-blue-950">
                    Tamil Nadu, India
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white border border-blue-900/10 rounded-3xl p-7 sm:p-9"
            >
              {submitted && (
                <div className="mb-5 flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-800">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
                  <p className="text-sm font-medium">{t.contact.formSuccess}</p>
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
                    {t.contact.formName}
                  </label>
                  <input
                    data-testid="contact-input-name"
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-xl border border-blue-900/15 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                    placeholder="Mathivanan"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
                    {t.contact.formEmail}
                  </label>
                  <input
                    data-testid="contact-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className="w-full rounded-xl border border-blue-900/15 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
                    {t.contact.formMessage}
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className="w-full rounded-xl border border-blue-900/15 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="btn-shine w-full inline-flex items-center justify-center gap-2.5 rounded-xl bg-blue-950 text-white px-6 py-4 font-bold hover:bg-blue-900 transition shadow-lg shadow-blue-950/20"
                >
                  <Send size={18} />
                  {t.contact.formSubmit}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Contact;
